import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { WorldSection } from './WorldPageHeader';
import { CopyTextButton } from './CopyTextButton';
import { buildGuidePendings, categoryLabel, formatGuidePendings, reasonLabel, type PendingExtras } from '../../utils/guidePendings';
import { fetchCsv, parseJson } from '../../utils/csv';
import { controlFile, packageFiles } from '../../data/world/package';
import { loadEventPositions } from '../../utils/eventPositions';
import type { WorldData } from '../../types/world';
const urlOf = (name: string) => packageFiles.find(f => f.filename === name)?.url;

/** Lee los archivos que el mundo cargado no incluye (31, 32 y la fila dataset de 00_control). */
async function loadExtras(): Promise<PendingExtras> {
  const errors: string[] = [];
  const get = async (name: string, url?: string) => {
    if (!url) {
      errors.push(`${name}: sin URL en el paquete`);
      return undefined;
    }
    try {
      const r = await fetchCsv(url);
      r.errors.slice(0, 3).forEach(e => errors.push(`${name}: ${e}`));
      return r.rows;
    } catch (e) {
      errors.push(`${name}: ${e instanceof Error ? e.message : 'error de descarga'}`);
      return undefined;
    }
  };
  const [ep, control] = await Promise.all([loadEventPositions(), get('00_control.csv', controlFile.url)]);
  errors.push(...ep.errors);
  const eventMonsters = ep.eventMonsters.length || !ep.errors.some(e => e.startsWith('31_')) ? ep.eventMonsters : undefined;
  const eventSpawnRules = ep.spawnRules.length || !ep.errors.some(e => e.startsWith('32_')) ? ep.spawnRules : undefined;
  let controlPending: unknown;
  if (control) {
    const ds = control.find(r => r.record_type === 'dataset');
    const details = ds?.details_json ? parseJson<Record<string, unknown>>(ds.details_json) : undefined;
    if (!ds) errors.push('00_control.csv: sin fila record_type = dataset');else if (!details) errors.push('00_control.csv: details_json de la fila dataset no es JSON válido');else if (details.pending_requirements === undefined) errors.push('00_control.csv: details_json no contiene pending_requirements');else controlPending = details.pending_requirements;
  }
  return {
    eventMonsters,
    eventSpawnRules,
    positions: ep.positions,
    controlPending,
    errors
  };
}

/** Lista consolidada de pendientes de toda la guía, con un único botón para copiarla. */
export function GuidePendings({
  w
}: {
  w: WorldData;
}) {
  const [extras, setExtras] = useState<PendingExtras | undefined>();
  useEffect(() => {
    let alive = true;
    loadExtras().then(x => alive && setExtras(x));
    return () => {
      alive = false;
    };
  }, []);
  const r = useMemo(() => extras ? buildGuidePendings(w, extras) : undefined, [w, extras]);
  const text = useMemo(() => r ? formatGuidePendings(r) : '', [r]);
  if (!r) {
    return <WorldSection id="pendientes" title="Pendientes de la guía" data-id="element-1273" data-mp="bjbuqgv01w9m">
        <p className="text-sm text-gray-400" role="status" data-id="element-1274" data-mp="gj6l8jav4ju5">Leyendo 31_event_monsters, 32_event_spawn_rules y 00_control.csv…</p>
      </WorldSection>;
  }
  const admin = r.pendings.filter(p => p.needsAdmin);
  const internal = r.pendings.length - admin.length;
  const counts = Object.entries(categoryLabel).map(([k, label]) => ({
    label,
    n: admin.filter(p => p.category === k).length
  })).filter(c => c.n);
  return <WorldSection id="pendientes" title="Pendientes de la guía" data-id="element-1275" data-mp="3r8o1wljpcb8m">
      <p className="text-sm text-gray-400" data-id="element-1276" data-mp="3m8a6c2zaetrh">
        Calculado en este navegador con la misma lógica que las fichas. Cada fila es una sola pregunta, aunque el dato aparezca en varias fichas. Responde con la clave y se integrará como «Confirmado por administración» con fecha.
      </p>

      {r.extrasErrors.length > 0 && <ul className="mt-3 rounded-xl border border-red-400/30 p-3 text-sm text-red-200" data-id="element-1277" data-mp="37gj2xjcoibio">
          {r.extrasErrors.map(e => <li key={e} data-id="element-1278" data-mp="2yhxo9zabvgn6">{e}</li>)}
        </ul>}

      <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-2" data-id="element-1279" data-mp="2yj8s1z3rwoea">
        <p className="font-cinzel text-2xl font-bold text-white" data-id="element-1280" data-mp="12ej6guy9gr6x">{admin.length} preguntas para administración</p>
        <p className="text-sm text-gray-400" data-id="element-1281" data-mp="16fbi1uulv86d">
          {internal} problemas internos · {r.resolvedByAdmin.length} respondidos · catálogo {r.catalog.published} publicados de {r.catalog.raw} ({r.catalog.excluded.length} excluidos)
        </p>
      </div>
      <ul className="mt-3 flex flex-wrap gap-2 text-sm" data-id="element-1282" data-mp="xlq4ufy39yz2">
        {counts.map(c => <li key={c.label} className="rounded-full border border-white/10 px-3 py-1 text-gray-300" data-id="element-1283" data-mp="iswj4cs84mly">
            {c.label} <span className="font-mono font-semibold text-white" data-id="element-1284" data-mp="9q3tsf2lqkyr">{c.n}</span>
          </li>)}
      </ul>

      <div className="mt-4" data-id="element-1285" data-mp="eox6w6dz89ye">
        <CopyTextButton text={text} label="Copiar todos los pendientes de la guía" data-id="element-1286" data-mp="eq89w3zlldog" />
      </div>

      <h3 className="mt-6 text-sm font-semibold text-white" data-id="element-1287" data-mp="2456f00kmbgvv">Bloque 1 · primeras 20 preguntas</h3>
      <ol className="mt-2 divide-y divide-white/5 rounded-2xl border border-white/10" data-id="element-1288" data-mp="2926lgcldqlni">
        {admin.slice(0, 20).map((p, i) => <li key={p.key} className="grid gap-1 px-4 py-3 text-sm md:grid-cols-[2rem_1fr]" data-id="element-1289" data-mp="2j7ek1g7cy606" data-mpi={i}>
            <span className="font-mono text-gray-500" data-id="element-1290" data-mp="2e9wlmjqyv96f">{i + 1}</span>
            <div className="min-w-0" data-id="element-1291" data-mp="2rzqdlaxrq9kd">
              <p className="font-semibold text-white" data-id="element-1292" data-mp="2s0rpfhmlz035">{p.name}</p>
              <p className="text-gray-300" data-id="element-1293" data-mp="2n7ozrkkycas8">{p.question}</p>
              <p className="mt-1 text-xs text-gray-500" data-id="element-1294" data-mp="31xnwdiwuze5l">
                {categoryLabel[p.category]} · {reasonLabel[p.reason]} · Evidencia: {p.evidence.join(', ')}
                {p.affects ? ` · afecta a ${p.affects}` : ''}
              </p>
              <p className="mt-1 flex flex-wrap gap-x-3 text-xs" data-id="element-1295" data-mp="3ax18ykga4iuj">
                {p.links.filter(x => x.startsWith('/')).slice(0, 3).map(to => <Link key={to} to={to} className="text-[#D4AF37] underline underline-offset-4" data-id="element-1296" data-mp="1gkbfg3ttpbno">{to}</Link>)}
                <span className="font-mono text-gray-600" data-id="element-1297" data-mp="1cjhr59cgxuu9">{p.key}</span>
              </p>
            </div>
          </li>)}
      </ol>

      <h3 className="mt-6 text-sm font-semibold text-white" data-id="element-1298" data-mp="3bpaphhvqtk4j">Secciones revisadas</h3>
      <ul className="mt-2 space-y-1 text-sm" data-id="element-1299" data-mp="3gnccuf07c5lt">
        {r.coverage.map(c => <li key={c.section} className={c.reviewed ? 'text-gray-300' : 'text-amber-200'} data-id="element-1300" data-mp="3lkm6k6umj3fg">
            {c.reviewed ? 'Revisada' : 'No revisada'} · {c.section} <span className="text-gray-500" data-id="element-1301" data-mp="3qidyh0v9qpjn">— {c.note}</span>
          </li>)}
      </ul>
    </WorldSection>;
}